import { useCallback, useMemo, useRef } from 'react';
import { useSSE } from '@/hooks/sse/useSSE';

type MaybePromise<T> = T | Promise<T>;

export type TopicSSEPayload = {
  triggerBy?: string;
  aiPipelineId?: string;
  prevAiPipelineId?: string;
  [key: string]: unknown;
};

export type TopicSSEMessage<
  TPayload extends TopicSSEPayload = TopicSSEPayload,
> = {
  topic?: string;
  payload?: TPayload;
  type?: string;
  [key: string]: unknown;
};

type TopicPipelineEventName = string | string[];

export type TopicPipelineEventNames = {
  start?: TopicPipelineEventName;
  complete?: TopicPipelineEventName;
  error?: TopicPipelineEventName;
  pipelineIdChanged?: TopicPipelineEventName;
};

export type TopicPipelineEventContext<
  TPayload extends TopicSSEPayload = TopicSSEPayload,
> = {
  data: TopicSSEMessage<TPayload>;
  eventName?: string;
  rawEventName?: string;
  topicId?: string;
  pipelineId?: string;
};

export type UseTopicPipelineSSEOptions<
  TPayload extends TopicSSEPayload = TopicSSEPayload,
> = {
  topic: string | null | undefined;
  events: TopicPipelineEventNames;
  enabled?: boolean;
  currentUserId?: string | null;
  onlyCurrentUser?: boolean;
  getPipelineId?: (data: TopicSSEMessage<TPayload>) => string | undefined;
  getPreviousPipelineId?: (
    data: TopicSSEMessage<TPayload>,
  ) => string | undefined;
  onStart?: (
    context: TopicPipelineEventContext<TPayload>,
  ) => MaybePromise<void>;
  onComplete?: (
    context: TopicPipelineEventContext<TPayload>,
  ) => MaybePromise<void>;
  onError?: (
    context: TopicPipelineEventContext<TPayload>,
  ) => MaybePromise<void>;
  onPipelineIdChanged?: (
    context: TopicPipelineEventContext<TPayload> & {
      previousPipelineId: string;
      nextPipelineId: string;
    },
  ) => MaybePromise<void>;
  onMessage?: (
    context: TopicPipelineEventContext<TPayload> & {
      eventName: string;
      rawEventName: string;
    },
  ) => MaybePromise<void>;
  onUnhandledMessage?: (
    context: TopicPipelineEventContext<TPayload> & {
      eventName: string;
      rawEventName: string;
    },
  ) => MaybePromise<void>;
};

export const getTopicId = (topic?: string) => topic?.split(':')[1];

const getDefaultPipelineId = <
  TPayload extends TopicSSEPayload = TopicSSEPayload,
>(
  data: TopicSSEMessage<TPayload>,
) => data.payload?.aiPipelineId;

const getDefaultPreviousPipelineId = <
  TPayload extends TopicSSEPayload = TopicSSEPayload,
>(
  data: TopicSSEMessage<TPayload>,
) => data.payload?.prevAiPipelineId;

const toEventNames = (eventName?: TopicPipelineEventName) =>
  typeof eventName === 'string' ? [eventName] : eventName ?? [];

/**
 * Topic 기반 SSE에서 start/complete/error + pipeline id 변경 이벤트를 공통 처리한다.
 *
 * 예: topic `talk-flow:${docId}`, events `talk-flow.organize.*`
 */
export const useTopicPipelineSSE = <
  TPayload extends TopicSSEPayload = TopicSSEPayload,
>({
  topic,
  events,
  enabled = true,
  currentUserId,
  onlyCurrentUser = Boolean(currentUserId),
  getPipelineId = getDefaultPipelineId,
  getPreviousPipelineId = getDefaultPreviousPipelineId,
  onStart,
  onComplete,
  onError,
  onPipelineIdChanged,
  onMessage,
  onUnhandledMessage,
}: UseTopicPipelineSSEOptions<TPayload>) => {
  const pipelineIdMapRef = useRef(new Map<string, string>());
  const topics = useMemo(() => (topic ? [topic] : []), [topic]);

  const getContext = useCallback(
    (
      data: TopicSSEMessage<TPayload>,
      eventName?: string,
      rawEventName?: string,
    ): TopicPipelineEventContext<TPayload> => {
      const rawPipelineId = getPipelineId(data);
      const mappedPipelineId = rawPipelineId
        ? pipelineIdMapRef.current.get(rawPipelineId)
        : undefined;

      return {
        data,
        eventName,
        rawEventName,
        topicId: getTopicId(data.topic),
        pipelineId: mappedPipelineId ?? rawPipelineId,
      };
    },
    [getPipelineId],
  );

  const handlers = useMemo(() => {
    const isTargetEvent = (data: TopicSSEMessage<TPayload>) => {
      if (!onlyCurrentUser) return true;
      return data.payload?.triggerBy === currentUserId;
    };

    const nextHandlers: Record<
      string,
      (data: TopicSSEMessage<TPayload>) => MaybePromise<void>
    > = {};

    toEventNames(events.start).forEach((eventName) => {
      if (!onStart) return;
      nextHandlers[eventName] = async (data) => {
        if (!isTargetEvent(data)) return;
        await onStart(getContext(data));
      };
    });

    toEventNames(events.complete).forEach((eventName) => {
      if (!onComplete) return;
      nextHandlers[eventName] = async (data) => {
        if (!isTargetEvent(data)) return;
        await onComplete(getContext(data));
      };
    });

    toEventNames(events.error).forEach((eventName) => {
      if (!onError) return;
      nextHandlers[eventName] = async (data) => {
        if (!isTargetEvent(data)) return;
        await onError(getContext(data));
      };
    });

    toEventNames(events.pipelineIdChanged).forEach((eventName) => {
      nextHandlers[eventName] = async (data) => {
        if (!isTargetEvent(data)) return;

        const previousPipelineId = getPreviousPipelineId(data);
        const nextPipelineId = getPipelineId(data);
        if (!previousPipelineId || !nextPipelineId) return;

        pipelineIdMapRef.current.set(nextPipelineId, previousPipelineId);

        await onPipelineIdChanged?.({
          ...getContext(data),
          previousPipelineId,
          nextPipelineId,
        });
      };
    });

    return nextHandlers;
  }, [
    currentUserId,
    events.complete,
    events.error,
    events.pipelineIdChanged,
    events.start,
    getPipelineId,
    getContext,
    getPreviousPipelineId,
    onlyCurrentUser,
    onComplete,
    onError,
    onPipelineIdChanged,
    onStart,
  ]);

  useSSE(topics, handlers, enabled && topics.length > 0, {
    onMessage: ({ data, eventName, rawEventName }) =>
      onMessage?.({
        ...getContext(data as TopicSSEMessage<TPayload>),
        eventName,
        rawEventName,
      }),
    onUnhandledMessage: ({ data, eventName, rawEventName }) =>
      onUnhandledMessage?.({
        ...getContext(data as TopicSSEMessage<TPayload>),
        eventName,
        rawEventName,
      }),
  });
};
